import type { AiRolePlayEventPayload, AudioEventPayload } from "@expert/expert-university";
import { useExpertUniversityStore, WsEvents } from "@expert/expert-university";
import { useEffect, useMemo } from "react";
import useWebSocket, { ReadyState } from "react-use-websocket";
import { env } from "@/config";
import { isSimulatedVoiceTask, useActiveTask } from "@/sdk";
import { useSessionStore } from "@/sdk/sessions/session.store";

export function useExpertUniversityWebsocket() {
    const { eventBus: expertUniversitySdkEventBus, setWSFunctions } = useExpertUniversityStore.getState();
    const activeTask = useActiveTask();
    const activeExpertUniversityTask = useMemo(() => {
        return isSimulatedVoiceTask(activeTask) ? activeTask : null;
    }, [activeTask]);

    const { profileId, sessionId } = activeExpertUniversityTask ?? {};

    const onOpen = () => {
        const websocketUrl = getWebSocket()?.url;
        if (expertUniversitySdkEventBus && websocketUrl) {
            expertUniversitySdkEventBus.emit("websocket-opened", { websocketUrl });
        }
    };

    const onClose = () => {
        const websocketUrl = getWebSocket()?.url;
        if (expertUniversitySdkEventBus && websocketUrl) {
            expertUniversitySdkEventBus.emit("websocket-closed", { websocketUrl });
        }
    };

    const onMessage = (event: MessageEvent) => {
        const data = JSON.parse(event.data as string) as AiRolePlayEventPayload;

        if (data.event === WsEvents.AUDIO && expertUniversitySdkEventBus) {
            const payload = data.eventPayload as AudioEventPayload;
            expertUniversitySdkEventBus.emit("audio-event-received", { audioBlob: payload.audioBlob });
        }
    };
    const { readyState, sendMessage, getWebSocket } = useWebSocket(
        env.expertUniversityWebsocketUrl ?? null,
        {
            queryParams: sessionId && profileId ? { sessionId, CustomerProfile: profileId } : undefined,
            shouldReconnect: (_closeEvent) => !!sessionId && !!profileId,
            reconnectAttempts: 5,
            reconnectInterval: 5000,
            onOpen,
            onClose,
            onMessage,
            onError: (error) => {
                console.log("WebSocket error:", error);
                return;
            },
        },
        !!sessionId && !!profileId,
    );
    useEffect(() => {
        if (readyState === ReadyState.OPEN) {
            setWSFunctions(sendMessage, getWebSocket);
        }
    }, [getWebSocket, readyState, sendMessage, setWSFunctions]);

    useEffect(() => {
        //End any session with non active simulated voice task
        if (!activeExpertUniversityTask && activeTask?.partner === "expertuniversity") {
            useSessionStore.getState().endSession(activeTask.id, "CallUnexpectedlyEnded");
        }
    }, [activeExpertUniversityTask, activeTask?.id, activeTask?.partner]);
}
